// Facture imprimable (A4), au format du modèle DOCC FA-2026-108.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { invoiceAPI, proformaAPI, settingsAPI } from '../services/api';
import { apiError, dateFr, fcfa } from '../utils/format';

const css = `
  @page { size: A4; margin: 10mm; }
  * { box-sizing: border-box; }
  body { margin: 0; background: #e9ecef; font-family: Arial, Helvetica, sans-serif; color: #111; }
  .toolbar { position: sticky; top: 0; z-index: 2; background: #0E3E66; color: #fff; padding: 10px 16px; display: flex; gap: 12px; align-items: center; }
  .toolbar button { background: #fff; color: #0E3E66; border: 0; padding: 8px 14px; border-radius: 4px; font-weight: bold; cursor: pointer; }
  .page { width: 190mm; height: 277mm; margin: 12px auto; background: #fff; padding: 6mm; position: relative; display: flex; flex-direction: column; overflow: hidden; }
  .top { display: flex; justify-content: space-between; }
  .emetteur { width: 48%; font-size: 12px; }
  .emetteur img { height: 64px; margin-left: 4px; }
  .emetteur .rs { font-weight: bold; margin: 6px 0 10px; font-size: 13px; }
  .emetteur .addr { margin-bottom: 18px; }
  .emetteur table td { padding: 3px 0; font-size: 12px; }
  .emetteur table td:first-child { width: 72px; }
  .right { width: 49%; }
  .fbox { border: 1px solid #333; }
  .fbox .title { text-align: center; font-size: 26px; font-weight: bold; padding: 4px 0; border-bottom: 1px solid #333; }
  .fbox table { width: 100%; border-collapse: collapse; font-size: 12px; text-align: center; }
  .fbox th { background: #e6e6e6; padding: 5px; border-right: 1px solid #333; border-bottom: 1px solid #333; }
  .fbox td { padding: 6px; border-right: 1px solid #333; }
  .fbox th:last-child, .fbox td:last-child { border-right: 0; }
  .client { border: 1px solid #333; border-radius: 8px; margin-top: 16px; padding: 10px 16px; min-height: 120px; font-size: 12.5px; line-height: 2; }
  .client .ville { padding-left: 46px; }
  .lines { flex: 1; margin-top: 24px; border: 1px solid #333; display: flex; flex-direction: column; min-height: 0; }
  /* Largeurs fixes : l'en-tête garde sa hauteur, le bas du tableau est prolongé par .rest */
  .lines table { width: 100%; border-collapse: collapse; font-size: 12.5px; table-layout: fixed; }
  .lines th { font-size: 11.5px; background: #f0f0f0; padding: 5px 4px; height: 26px; line-height: 1.2; border-bottom: 1px solid #333; border-right: 1px solid #333; vertical-align: middle; }
  .lines td { padding: 5px 4px; border-right: 1px solid #333; vertical-align: top; height: 26px; word-wrap: break-word; }
  .lines th:last-child, .lines td:last-child { border-right: 0; }
  .lines .rest { flex: 1; min-height: 0; }
  .lines .rest table { height: 100%; }
  .lines .rest td { height: auto; padding: 0; }
  .num { text-align: right; white-space: nowrap; }
  .c { text-align: center; }
  .bottom { display: flex; justify-content: space-between; align-items: flex-start; margin-top: 18px; font-size: 12.5px; }
  .bank { font-size: 12px; min-width: 0; }
  .bank b { font-size: 12px; }
  .totals { border-collapse: separate; border-spacing: 2px 0; font-size: 11.5px; flex-shrink: 0; }
  .totals th { font-weight: normal; border: 1px solid #333; padding: 4px 5px; white-space: nowrap; }
  .totals td { border: 1px solid #333; padding: 4px 5px; text-align: right; min-width: 76px; }
  .totals .net { font-weight: bold; }
  /* Zone réservée au cachet et à la signature (le tableau des lignes se raccourcit d'autant) */
  .sign { display: flex; justify-content: flex-end; margin-top: 5mm; flex-shrink: 0; }
  .sigbox { width: 78mm; height: 40mm; border: 1px solid #888; border-radius: 4px; position: relative; padding: 4px 8px; font-size: 11px; color: #444; }
  .sigbox .valid { position: absolute; left: 8px; right: 8px; bottom: 4px; font-size: 10.5px; color: #12284C; font-weight: bold; text-align: center; }
  .sigbox .stamp { position: absolute; left: 50%; top: 55%; transform: translate(-50%, -50%); max-width: 66mm; max-height: 30mm; }
  .mentions { font-size: 9px; color: #444; margin-top: 8px; }
  /* Filigrane DOCC permanent (factures et proformas), au centre de la page */
  .wm-docc { position: absolute; top: 52%; left: 50%; transform: translate(-50%, -50%) rotate(-30deg); font-size: 170px; font-weight: 900; letter-spacing: 12px; color: rgba(18, 40, 76, .07); pointer-events: none; white-space: nowrap; z-index: 1; user-select: none; }
  .watermark { position: absolute; z-index: 2; top: 30%; left: 50%; transform: translate(-50%, -50%) rotate(-28deg); font-size: 90px; font-weight: bold; color: rgba(200, 0, 0, .12); pointer-events: none; white-space: nowrap; }
  @media print { body { background: #fff; } .toolbar { display: none; } .page { margin: 0; width: auto; height: 274mm; padding: 0; } }
`;

/** Impression A4 d'une facture ou d'une facture proforma (kind="proforma"). */
const FacturePrint = ({ kind = 'facture' }) => {
  const isPf = kind === 'proforma';
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [s, setS] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([(isPf ? proformaAPI : invoiceAPI).get(id), settingsAPI.get()])
      .then(([i, st]) => {
        const d = i.data;
        // Une proforma s'imprime avec la même mise en page : date = date de la proforma, échéance = validité
        setInv(isPf ? { ...d, date_facture: d.date_proforma, date_echeance: d.date_validite } : d);
        setS(st.data);
        document.title = d.numero || (isPf ? 'Proforma brouillon' : 'Facture brouillon');
      })
      .catch((err) => setError(apiError(err)));
  }, [id, isPf]);

  if (error) return <div style={{ padding: 24 }}>{error}</div>;
  if (!inv || !s) return <div style={{ padding: 24 }}>Chargement…</div>;

  const watermark = inv.statut === 'BROUILLON' ? 'BROUILLON' : inv.statut === 'ANNULEE' ? 'ANNULÉE' : null;
  const remise = inv.montant_remise > 0;
  return (
    <>
      <style>{css}</style>
      <div className="toolbar">
        <strong>{inv.numero || 'Brouillon'}</strong>
        <span style={{ marginRight: 'auto' }}>{inv.client_nom}</span>
        <button onClick={() => window.print()}>Imprimer / PDF</button>
      </div>
      <div className="page">
        <div className="wm-docc" aria-hidden="true">DOCC</div>
        {watermark && <div className="watermark">{watermark}</div>}
        <div className="top">
          <div className="emetteur">
            <img src="/logo-docc.png" alt="DOCC" />
            <div className="rs">{s.raison_sociale}</div>
            <div className="addr">{s.adresse}</div>
            <table><tbody>
              {s.telephone && <tr><td>Tél :</td><td>{s.telephone}</td></tr>}
              {s.email && <tr><td>Email :</td><td>{s.email}</td></tr>}
              {s.rccm && <tr><td>RCCM :</td><td>{s.rccm}</td></tr>}
              {s.ninea && <tr><td>N° TVA :</td><td>{s.ninea}</td></tr>}
            </tbody></table>
          </div>
          <div className="right">
            <div className="fbox">
              <div className="title" style={isPf ? { fontSize: 22 } : undefined}>{isPf ? 'Facture proforma' : 'Facture'}</div>
              <table>
                <thead><tr><th>Numéro</th><th>Date</th><th>{isPf ? 'Valable jusqu\'au' : 'Echéance'}</th></tr></thead>
                <tbody><tr><td>{inv.numero || '—'}</td><td>{dateFr(inv.date_facture)}</td><td>{dateFr(inv.date_echeance)}</td></tr></tbody>
              </table>
            </div>
            <div className="client">
              <div>Société</div>
              <div>{inv.client_nom}</div>
              {inv.client_adresse && <div>{inv.client_adresse}</div>}
              {inv.client_ville && <div className="ville">{inv.client_ville}</div>}
              {inv.client_ninea && <div style={{ fontSize: 11 }}>NINEA : {inv.client_ninea}</div>}
            </div>
          </div>
        </div>

        <div className="lines">
          <table>
            <thead>
              <tr>
                <th style={{ width: '10%' }}>Code</th><th style={{ width: '44%' }}>Désignation</th><th style={{ width: '8%' }}>Qté</th>
                <th style={{ width: '13%' }}>Prix HT</th><th style={{ width: '10%' }}>Taux TVA</th><th style={{ width: '15%' }}>Total TTC</th>
              </tr>
            </thead>
            <tbody>
              {inv.objet && <tr><td /><td>{inv.objet}</td><td /><td /><td /><td /></tr>}
              {inv.lines.map((l) => (
                <tr key={l.id}>
                  <td>{l.code}</td>
                  <td>{l.designation}</td>
                  <td className="c">{l.quantite}</td>
                  <td className="num">{fcfa(l.prix_unitaire_ht)}</td>
                  <td className="num">{Number(l.taux_tva)}%</td>
                  <td className="num">{fcfa(l.total_ttc)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Prolonge les colonnes jusqu'aux totaux (une seule ligne : pas de répartition de hauteur sur l'en-tête) */}
          <div className="rest">
            <table><colgroup><col style={{ width: '10%' }} /><col style={{ width: '44%' }} /><col style={{ width: '8%' }} /><col style={{ width: '13%' }} /><col style={{ width: '10%' }} /><col style={{ width: '15%' }} /></colgroup>
              <tbody><tr><td /><td /><td /><td /><td /><td /></tr></tbody></table>
          </div>
        </div>

        <div className="bottom">
          <div className="bank">
            {/* Coordonnées bancaires : factures et proformas */}
            {s.banque && <b>{s.banque}</b>}
            {s.iban && <div>IBAN:<b>{s.iban}</b></div>}
          </div>
          <table className="totals">
            <thead><tr>
              {remise && <><th>Montant brut HT</th><th>Remise {String(inv.remise_pct).replace('.', ',')} %</th></>}
              <th>Total HT</th><th>Total TVA</th>
              {isPf ? <th className="net">Total TTC</th> : <><th>Total TTC</th><th>Total Réglé</th><th className="net">Net à payer</th></>}
            </tr></thead>
            <tbody><tr>
              {remise && <><td>{fcfa(inv.total_brut_ht)}</td><td>-{fcfa(inv.montant_remise)}</td></>}
              <td>{fcfa(inv.total_ht)}</td><td>{fcfa(inv.total_tva)}</td>
              {isPf ? <td className="net">{fcfa(inv.total_ttc)}</td> : (
                <><td>{fcfa(inv.total_ttc)}</td><td>{fcfa(inv.total_regle)}</td><td className="net">{fcfa(inv.net_a_payer)}</td></>
              )}
            </tr></tbody>
          </table>
        </div>
        {isPf && (
          <div className="mentions" style={{ fontSize: 11, color: '#111' }}>
            {inv.conditions && <div><b>Conditions :</b> {inv.conditions}</div>}
            <div>Offre valable jusqu'au {dateFr(inv.date_echeance)}. Cette facture proforma ne vaut pas facture.</div>
          </div>
        )}
        <div className="sign">
          <div className="sigbox">
            Cachet et signature
            {inv.valide_nom && inv.statut !== 'BROUILLON' && <div className="valid">Validé par {inv.valide_nom}</div>}
            {s.afficher_cachet && s.cachet_image && inv.statut !== 'BROUILLON' && inv.statut !== 'ANNULEE' && inv.statut !== 'REFUSEE' && (
              <img className="stamp" src={s.cachet_image} alt="Cachet" />
            )}
          </div>
        </div>
        {s.mentions && <div className="mentions">{s.mentions}</div>}
      </div>
    </>
  );
};

export default FacturePrint;
